<template>
  <div class="expedientes-shell">
    <div v-show="!isContentReady" class="card">
      <div class="card-body text-center py-5 loader-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="text-muted mt-3">Cargando expediente...</p>
      </div>
    </div>

    <div v-show="isContentReady" class="card expedientes-card">
      <div class="expedientes-hero">
        <div class="hero-content">
          <div class="hero-left">
            <div class="hero-title-section">
              <p class="eyebrow">Panel clínico</p>
              <h3>Expedientes Médicos Electrónicos</h3>
              <p class="subtitle">Accede rápido a tus pacientes, arrastra para reordenar y abre su historial.</p>
            </div>
            <div class="hero-stats-actions">
              <div class="stat-pill">
                <i class="fas fa-users"></i>
                <span>{{ storeData.patients.length }} pacientes</span>
              </div>
              <div class="stats-divider"></div>
              <div class="hero-actions">
                <button class="btn-ghost" title="Buscar paciente" @click="openPacientes">
                  <i class="fa-regular fa-magnifying-glass"></i>
                  <span>Buscar</span>
                </button>
                <button class="btn-primary" title="Nuevo paciente" @click="openCreatePatientModal">
                  <i class="fas fa-user-plus"></i>
                  <span>Nuevo</span>
                </button>
                <button class="btn-ghost" :disabled="!showScrollLeft" title="Anterior" @click="scrollLeft">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <button class="btn-ghost" :disabled="!showScrollRight" title="Siguiente" @click="scrollRight">
                  <i class="fas fa-chevron-right"></i>
                </button>
                <button class="btn-ghost" :title="isScrollWrapperVisible ? 'Ocultar carrusel' : 'Mostrar carrusel'" @click="toggleScrollWrapper">
                  <i class="fas" :class="isScrollWrapperVisible ? 'fa-eye-slash' : 'fa-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="patientRecord" class="hero-patient-info">
            <div class="patient-info-header">
              <i class="fas fa-user-check"></i>
              <span class="patient-id">Paciente Activo #{{ patientRecord.id }}</span>
            </div>
            <div class="patient-info-body">
              <div class="patient-info-name">{{ patientRecord.last_name }} {{ patientRecord.first_name }}</div>
              <div class="patient-info-details">
                <span><i class="fas fa-venus-mars"></i> {{ getGenderLabel(patientRecord.gender) }}</span>
                <span><i class="fas fa-birthday-cake"></i> {{ calculateAge(patientRecord.birthdate || patientRecord.date_of_birth) }} años</span>
                <span><i class="fas fa-calendar"></i> {{ formatBirthdate(patientRecord.birthdate || patientRecord.date_of_birth) }}</span>
                <span><i class="fas fa-phone"></i> {{ getPhone(patientRecord) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-body p-0 m-0">
        <transition :name="shouldAnimate ? 'scroll-wrapper' : ''">
          <div v-if="isScrollWrapperVisible" class="rail-wrapper">
            <div ref="scrollContainer" class="rail-scroll" @dragover.prevent @drop="onDrop">
              <div class="rail-content">
                <div
                  v-for="(patient, index) in storeData.patients"
                  :key="patient.id"
                  class="patient-item patient-card"
                  :class="{
                    'opacity-patient': isPatientInEdge(index),
                    'selected': patient.id === storeData.lastSelectedOrAddedId,
                    'dragging-card': isDragging && draggedIndex === index
                  }"
                  draggable="true"
                  @click="selectPatientAndUpdateRecord(patient.id)"
                  @dragstart="onDragStart($event, index)"
                  @dragover.prevent="onDragOver($event, index)"
                >
                  <div
                    v-if="isDragging && dropTargetIndex === index && index !== draggedIndex"
                    class="drop-placeholder-line drop-target-active"
                  >
                    <div class="drop-placeholder__content">
                      <i class="fas fa-arrow-down"></i>
                      Suelta aquí
                    </div>
                  </div>

                  <span class="badge-close" title="Eliminar paciente" @click.stop="removePatient(patient.id)">
                    <i class="fas fa-times"></i>
                  </span>

                  <div class="patient-top">
                    <div class="avatar-shell" :class="{ 'is-active': patient.id === storeData.lastSelectedOrAddedId }">
                      <img :alt="patient.id" :src="getPatientPhotoUrl(patient)" />
                    </div>
                    <div class="patient-meta">
                      <div class="patient-name-row patient-lastname">
                        <span class="patient-fullname">{{ patient.last_name }}</span>
                      </div>
                      <div class="patient-name-row patient-firstname">
                        <span class="patient-fullname">{{ patient.first_name }}</span>
                      </div>
                      <div class="patient-brief">
                        <span><i class="fas fa-birthday-cake"></i> {{ calculateAge(patient.birthdate || patient.date_of_birth) }} años</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="isDragging && dropTargetIndex === storeData.patients.length && draggedIndex !== null"
                  class="drop-placeholder-line drop-target-active"
                >
                  <div class="drop-placeholder__content">
                    <i class="fas fa-arrow-down"></i>
                    Suelta aquí
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <div v-if="patientRecord && isContentReady" class="patient-content-wrapper">
          <PatientRecord :patient-id="patientRecord.id" />
        </div>

        <div v-else-if="!patientRecord && isContentReady" class="empty-state-container">
          <EmptyState
            type="medical-notes"
            title="Selecciona un paciente"
            description="Elige un paciente de la lista superior para ver su expediente médico completo"
            primary-action="Buscar Paciente"
            primary-icon="fa-solid fa-magnifying-glass"
            :animate="false"
            @create-first="openPacientes"
          />
        </div>
      </div>
    </div>
  </div>

  <PatientModal v-model="showPatientModal" :is-edit-mode="false" />
</template>

<script setup>
import { ref, onMounted, watch, nextTick, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, loadFromLocalStorage, saveToLocalStorage } from '@/store/Expedientes/pacientes.js';
import { patientEventBus } from '@/utils/eventBus.js';
import profilePicture from '@/assets/img/profile-picture.png';
import profilePictureWomen from '@/assets/img/profile-picture-women.png';

import '@/assets/css/Expedientes.css';
// ✅ Import corregido - PatientRecord movido a MedicalRecord/Patient/PatientRecordSummary
import PatientRecord from '@/components/MedicalRecord/Patient/PatientRecordSummary.vue';
import EmptyState from '@/components/EmptyState.vue';
import PatientModal from '@/components/PatientModal.vue';

const { storeData, removePatientId, getPatientDataById, selectPatient, movePatient } = useStore();
const router = useRouter();

const patientRecord = ref(null);
const showScrollLeft = ref(false);
const showScrollRight = ref(false);
const draggedIndex = ref(null);
const isDragging = ref(false);
const isScrollWrapperVisible = ref(true);
const shouldAnimate = ref(false);
const dropTargetIndex = ref(null);
const isContentReady = ref(false); // Controla cuando mostrar el contenido

const scrollContainer = ref(null);
const patientItems = ref([]);
const showPatientModal = ref(false);

const getProfilePicture = (gender) => {
  return (gender === 'F' || gender === 'female') ? profilePictureWomen : profilePicture;
};

const getPatientPhotoUrl = (patient) => {
  return patient?.photo_url || getProfilePicture(patient?.gender);
};

let eventBusUnsubscribe = null;

const calculateAge = (birthDate) => {
  if (!birthDate) return 'N/A';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const openPacientes = () => {
  router.push({ name: 'Pacientes' });
};

const openCreatePatientModal = () => {
  showPatientModal.value = true;
};

const getGenderLabel = (gender) => {
  if (!gender) return "-";
  const g = gender.toString().toUpperCase();
  if (g === "F" || g === "FEMALE") return "Femenino";
  if (g === "M" || g === "MALE") return "Masculino";
  return gender;
};

const formatBirthdate = (birthDate) => {
  if (!birthDate) return "Sin fecha";
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return birthDate;
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};

const getPhone = (patient) => {
  return patient?.whatsapp || patient?.phone || patient?.phone_number || patient?.mobile || "Sin teléfono";
};

const selectPatientAndUpdateRecord = (id) => {
  selectPatient(id);
  const patientData = getPatientDataById(id);
  patientRecord.value = patientData;
};

const scrollSmoothly = (amount) => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: amount, behavior: 'smooth' });
  }
};

const scrollLeft = () => {
  scrollSmoothly(-200);
};

const scrollRight = () => {
  scrollSmoothly(200);
};

const removePatient = (id) => {
  removePatientId(id);
  updateScrollIcons();
  saveToLocalStorage('storeData', storeData.value);
};

const onDragStart = (event, index) => {
  isDragging.value = true;
  draggedIndex.value = index;
  dropTargetIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
};

const onDragOver = (event, index) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';

  if (index !== dropTargetIndex.value && index !== draggedIndex.value) {
    dropTargetIndex.value = index;
  }
};

const onDrop = (event) => {
  event.preventDefault();
  if (draggedIndex.value !== null && dropTargetIndex.value !== null) {
    const targetIndex = dropTargetIndex.value;
    if (draggedIndex.value !== targetIndex) {
      movePatient(draggedIndex.value, targetIndex);
    }
    selectPatient(storeData.value.patients[targetIndex].id);
  }
  resetDragState();
};

const resetDragState = () => {
  isDragging.value = false;
  draggedIndex.value = null;
  dropTargetIndex.value = null;
  saveToLocalStorage('storeData', storeData.value);
};

const loadPatientsFromLocalStorage = () => {
  const storedData = loadFromLocalStorage('storeData', {
    patients: [],
    lastSelectedOrAddedId: null
  });
  storeData.value = storedData;
};

watch(storeData, async (newData, oldData) => {
  if (newData.lastSelectedOrAddedId) {
    const patientData = getPatientDataById(newData.lastSelectedOrAddedId);
    patientRecord.value = patientData;

    if (oldData && newData.lastSelectedOrAddedId !== oldData.lastSelectedOrAddedId) {
      await nextTick();
      await nextTick();

      if (scrollContainer.value) {
        patientItems.value = scrollContainer.value.querySelectorAll('.patient-item') || [];
      }

      if (scrollContainer.value) {
        scrollContainer.value.scrollLeft = scrollContainer.value.scrollWidth;
      }
    }
  } else {
    patientRecord.value = null;
  }
}, { deep: true, immediate: true });

onMounted(async () => {
  loadPatientsFromLocalStorage();

  if (storeData.value.patients.length === 0) {
    router.push({ name: 'Pacientes' });
    return;
  }

  if (storeData.value.lastSelectedOrAddedId) {
    patientRecord.value = getPatientDataById(storeData.value.lastSelectedOrAddedId);
  }

  eventBusUnsubscribe = patientEventBus.subscribe((patientId, event) => {
    if (event.action === 'patient-photo-updated' || event.action === 'patient-photo-deleted') {
      const patientIndex = storeData.value.patients.findIndex(p => p.id === patientId);
      if (patientIndex !== -1) {
        if (event.action === 'patient-photo-updated') {
          storeData.value.patients[patientIndex].photo_url = event.photo_url;
        } else {
          storeData.value.patients[patientIndex].photo_url = null;
        }

        if (patientRecord.value?.id === patientId) {
          patientRecord.value.photo_url = event.action === 'patient-photo-updated' ? event.photo_url : null;
        }

        saveToLocalStorage('storeData', storeData.value);
      }
    }
  });

  await nextTick();

  updateScrollIcons();
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', updateScrollIcons);
  }
  setGradientOpacity();

  patientItems.value = scrollContainer.value?.querySelectorAll('.patient-item') || [];

  const storedVisibility = localStorage.getItem('isScrollWrapperVisible');
  if (storedVisibility !== null) {
    isScrollWrapperVisible.value = JSON.parse(storedVisibility);
  }

  await nextTick();

  setTimeout(() => {
    isContentReady.value = true;
  }, 150);
});

onUnmounted(() => {
  if (eventBusUnsubscribe) {
    eventBusUnsubscribe();
  }
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', updateScrollIcons);
  }
});

const isPatientInEdge = (index) => {
  if (!scrollContainer.value || patientItems.value.length === 0) return false;
  if (!patientItems.value[index]) return false;

  const containerRect = scrollContainer.value.getBoundingClientRect();
  const itemRect = patientItems.value[index].getBoundingClientRect();

  return (
    (itemRect.left < containerRect.left + 50 && showScrollLeft.value) ||
    (itemRect.right > containerRect.right - 50 && showScrollRight.value)
  );
};

const setGradientOpacity = () => {
  const scrollLeft = scrollContainer.value.scrollLeft;
  const maxScrollLeft = scrollContainer.value.scrollWidth - scrollContainer.value.clientWidth;

  scrollContainer.value.style.setProperty('--before-opacity', scrollLeft > 0 ? '1' : '0');
  scrollContainer.value.style.setProperty('--after-opacity', scrollLeft < maxScrollLeft ? '1' : '0');
};

const updateScrollIcons = () => {
  if (scrollContainer.value && storeData.value.patients.length > 0) {
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
    showScrollLeft.value = scrollLeft > 0;
    showScrollRight.value = scrollLeft + clientWidth < scrollWidth;
    setGradientOpacity();
  } else {
    showScrollLeft.value = false;
    showScrollRight.value = false;
    router.push({ name: 'Pacientes' });
  }
};

const toggleScrollWrapper = () => {
  shouldAnimate.value = true;
  isScrollWrapperVisible.value = !isScrollWrapperVisible.value;
  localStorage.setItem('isScrollWrapperVisible', JSON.stringify(isScrollWrapperVisible.value));
};
</script>

<style scoped>
.expedientes-shell {
  padding: 0.5rem;
}

.expedientes-card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.expedientes-hero {
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid #e2e8f0;
}

.hero-content {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: center;
}

.hero-left {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.hero-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.eyebrow {
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 700;
  margin: 0;
  color: #6366f1;
}

.expedientes-hero h3 {
  margin: 0;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: -0.2px;
  line-height: 1.2;
  color: #0f172a;
}

.subtitle {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}

.hero-stats-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-stats {
  display: flex;
  gap: 8px;
}

.stats-divider {
  width: 1px;
  height: 32px;
  background: linear-gradient(to bottom, transparent, #cbd5e1 20%, #cbd5e1 80%, transparent);
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  color: #0f172a;
  font-weight: 700;
  font-size: 13px;
  border: 1px solid #cbd5e1;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.stat-pill i {
  color: #6366f1;
}

.hero-patient-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: all 0.2s ease;
}

.hero-patient-info:hover {
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  border-color: #cbd5e1;
}

.patient-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
}

.patient-info-header i {
  font-size: 11px;
  color: #6366f1;
  opacity: 0.7;
}

.patient-id {
  color: #475569;
  font-weight: 700;
}

.patient-info-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.patient-info-name {
  font-weight: 700;
  font-size: 16px;
  color: #1e293b;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.patient-info-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
}

.patient-info-details span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 500;
  transition: all 0.15s ease;
}

.patient-info-details span:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.patient-info-details i {
  color: #6366f1;
  font-size: 11px;
  opacity: 0.8;
}

.hero-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.btn-ghost:hover:not(:disabled) {
  border-color: #6366f1;
  color: #6366f1;
  background: #f8fafc;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  transform: translateY(-1px);
}

.btn-ghost:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-ghost:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.btn-ghost i {
  font-size: 14px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
  transform: translateY(-1px);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.btn-primary i {
  font-size: 14px;
}

.rail-wrapper {
  padding: 14px 16px 6px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.rail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}

.pill-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
}

.rail-controls {
  display: flex;
  gap: 6px;
}

.rail-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
}

.rail-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.rail-scroll {
  overflow-x: auto;
  padding: 6px 2px 14px;
  scrollbar-width: none; /* Oculta scrollbar en navegadores soportados */
  scrollbar-color: transparent transparent;
}

.rail-scroll::-webkit-scrollbar {
  height: 0; /* Oculta scrollbar en WebKit */
}

.rail-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.rail-scroll::-webkit-scrollbar-thumb {
  background: transparent;
}

.rail-content {
  display: flex;
  gap: 12px;
}

.patient-card {
  min-width: 280px;
  max-width: 320px;
  background: linear-gradient(135deg, #ffffff, #fafdff);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  position: relative;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.patient-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 30px rgba(15, 23, 42, 0.14);
  border-color: #cbd5e1;
  background: #f8fafc;
}

.patient-card.selected {
  border-color: #22d3ee;
  box-shadow: 0 18px 32px rgba(34, 211, 238, 0.25);
}

.badge-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fee2e2;
  color: #b91c1c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  z-index: 2;
  border: 1px solid #fecdd3;
  box-shadow: 0 8px 18px rgba(248, 113, 113, 0.18);
  transition: all 0.18s ease, transform 0.12s ease;
}

.badge-close:hover {
  background: #fca5a5;
  color: #7f1d1d;
  border-color: #f87171;
  box-shadow: 0 10px 22px rgba(185, 28, 28, 0.22);
  transform: translateY(-1px);
}

.patient-top {
  display: flex;
  gap: 12px;
  align-items: center;
}

.avatar-shell {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  border: 2px solid #e2e8f0;
  background: #f8fafc;
}

.avatar-shell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-shell.is-active {
  border-color: #22d3ee;
  box-shadow: 0 8px 16px rgba(34, 211, 238, 0.25);
}

.chip-id {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: #0f172a;
  color: #fff;
  border-radius: 8px;
  padding: 4px 6px;
  font-size: 11px;
}

.patient-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding-left: 2px;
}

.patient-name-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  line-height: 1.2;
}

.patient-fullname {
  font-weight: 700;
  color: #0f172a;
  font-size: 14px;
  letter-spacing: 0.2px;
}

.patient-lastname .patient-fullname {
  text-transform: uppercase;
  color: #0b172e;
}

.patient-firstname .patient-fullname {
  font-weight: 600;
  color: #1f2937;
}

.patient-brief {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 12px;
  margin-top: 4px;
}

.patient-brief i {
  color: #22d3ee;
}

.drop-placeholder-line {
  width: calc(100% - 8px);
  margin: 6px auto;
  padding: 8px 10px;
  border: 2px dashed #4f46e5;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.08), rgba(34, 211, 238, 0.08));
  color: #1f2937;
  box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.15);
}

.drop-placeholder__content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #0f172a;
}

.drop-placeholder__content i {
  color: #4f46e5;
}

.drop-target-active {
  transform: translateY(1px) scale(1.01);
  border-color: #2dce89;
  background: linear-gradient(135deg, rgba(45, 206, 137, 0.12), rgba(34, 211, 238, 0.12));
  box-shadow: inset 0 0 0 1px rgba(45, 206, 137, 0.25), 0 6px 14px rgba(34, 211, 238, 0.18);
}

@media (max-width: 1024px) {
  .hero-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .hero-patient-info {
    order: -1;
  }

  .hero-stats-actions {
    flex-wrap: wrap;
  }

  .stats-divider {
    display: none;
  }
}

@media (max-width: 768px) {
  .expedientes-hero {
    padding: 16px;
  }

  .hero-content {
    gap: 12px;
  }

  .hero-left {
    gap: 12px;
  }

  .expedientes-hero h3 {
    font-size: 18px;
  }

  .subtitle {
    font-size: 12px;
  }

  .hero-stats-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
  }

  .hero-actions {
    width: 100%;
    justify-content: space-between;
  }

  .btn-ghost span,
  .btn-primary span {
    display: none;
  }

  .btn-ghost,
  .btn-primary {
    padding: 10px;
    min-width: 40px;
    flex: 1;
  }

  .patient-info-details {
    flex-direction: column;
    gap: 6px;
  }

  .rail-content {
    gap: 10px;
  }

  .patient-card {
    min-width: 180px;
  }
}
</style>